import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import apiClient from '@/services/api/client';
import { 
  DineAIImpactDashboard,
  TopPerformersSection,
  AIPerformanceAnalysis,
  AIPerformanceAnalysisInteractive,
  ExtendedPerformanceCards
} from '@/components/analytics';
import type { 
  AnalyticsData, 
  SeasonalMenu, 
  DateRangeOption 
} from '@/types/analytics';

export default function AnalyticsV2() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Core state - single source of truth
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnalyticsData | null>(null);
  
  // Combined data for annual projection
  const [combinedMonthlyData, setCombinedMonthlyData] = useState<any>(null);
  const [combinedQuarterlyData, setCombinedQuarterlyData] = useState<any>(null);
  
  // Menu and date state
  const [menuType, setMenuType] = useState<'regular' | 'seasonal'>('regular');
  
  // Seasonal menu state
  const [hasActiveSeasonalMenu, setHasActiveSeasonalMenu] = useState(false);
  const [activeSeasonalMenuId, setActiveSeasonalMenuId] = useState<number | null>(null);
  const [activeSeasonalMenuName, setActiveSeasonalMenuName] = useState<string>('Seasonal Menu');
  
  // Restaurant data
  const [restaurantData, setRestaurantData] = useState<any>(null);
  
  // Check if this is a demo user - hide historical warnings for demos
  const isDemoUser = localStorage.getItem('isDemo') === 'true';
  
  // Check URL parameters
  const viewParam = searchParams.get('view');

  // Initialize on mount
  useEffect(() => {
    console.log('🎬 AnalyticsV2 mounted, starting initialization...');
    initializeAnalytics();
  }, []);
  
  // Handle navigation from dashboard AI insights
  useEffect(() => {
    if (viewParam === 'ai-insights') {
      // Scroll to AI Menu Intelligence section after a short delay
      setTimeout(() => {
        const element = document.getElementById('ai-menu-intelligence');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, [viewParam]);

  // Handle menu type changes - fetch all periods at once
  useEffect(() => {
    // Skip initial render until initialization is complete
    if (!restaurantData) {
      console.log('⏳ Waiting for initialization to complete...');
      return;
    }
    
    // Only fetch if we have required data
    if (menuType === 'seasonal' && !activeSeasonalMenuId) {
      console.log('⏳ Waiting for seasonal menu ID...');
      return;
    }
    
    console.log('🔄 Analytics useEffect triggered:', {
      menuType,
      activeSeasonalMenuId,
      hasActiveSeasonalMenu,
      restaurantData: !!restaurantData
    });
    
    fetchAllPeriodsData();
  }, [menuType, activeSeasonalMenuId, restaurantData]);
  
  // Fetch combined data when we have what we need
  useEffect(() => {
    if (restaurantData && data) {
      console.log('📊 Fetching combined data for annual projection...');
      fetchCombinedData();
    }
  }, [data, restaurantData, activeSeasonalMenuId]);


  const initializeAnalytics = async () => {
    console.log('🔧 initializeAnalytics called');
    try {
      // Fetch seasonal menus and profile in parallel
      console.log('📡 Fetching seasonal menus and profile...');
      const [seasonalMenusRes, profileRes] = await Promise.all([
        apiClient.get('/seasonal-menus').catch((err) => {
          console.error('Failed to fetch seasonal menus:', err);
          return { data: [] };
        }),
        apiClient.get('/restaurants/profile').catch((err) => {
          console.error('Failed to fetch profile:', err);
          return null;
        })
      ]);
      console.log('✅ Initial data fetched');

      // Check for seasonal menus
      const menus = Array.isArray(seasonalMenusRes.data) 
        ? seasonalMenusRes.data 
        : seasonalMenusRes.data.seasonal_menus || [];
      
      console.log('📋 All seasonal menus received:', menus);
      
      const activeMenu = menus
        .filter((menu: SeasonalMenu) => menu.is_active)
        .sort((a: SeasonalMenu, b: SeasonalMenu) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0];

      if (activeMenu) {
        console.log('🍽️ Active seasonal menu found:', {
          id: activeMenu.seasonal_menu_id,
          name: activeMenu.menu_name,
          active: activeMenu.is_active
        });
        setHasActiveSeasonalMenu(true);
        setActiveSeasonalMenuId(activeMenu.seasonal_menu_id);
        setActiveSeasonalMenuName(activeMenu.menu_name || 'Seasonal Menu');
      } else {
        console.log('⚠️ No active seasonal menu found in:', menus);
      }

      // Set restaurant data
      if (profileRes) {
        setRestaurantData(profileRes.data);
      }
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
      setError('Failed to initialize analytics');
    }
  };

  const fetchAllPeriodsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build base params
      const baseParams: any = { 
        menu_type: menuType
      };
      
      if (menuType === 'seasonal') {
        if (!activeSeasonalMenuId) {
          console.warn('⚠️ No seasonal menu ID available');
          setError('Seasonal menu not properly loaded. Please refresh the page.');
          setLoading(false);
          return;
        }
        baseParams.seasonal_menu_id = activeSeasonalMenuId;
        console.log('🍽️ Using seasonal menu ID:', activeSeasonalMenuId);
      }

      console.log('📊 Fetching all periods with params:', JSON.stringify(baseParams));

      // Fetch all three periods with timeout and error handling
      const fetchWithTimeout = (promise: Promise<any>, timeoutMs: number = 10000) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
          )
        ]);
      };

      console.log('🚀 Starting sequential fetches to reduce backend load...');
      
      // Fetch sequentially to avoid overwhelming the backend
      let weeklyData = null, aiInsights = null, monthlyResult = null, quarterlyResult = null;
      
      try {
        console.log('📊 Fetching 7-day data from dashboard endpoint...');
        // Use dashboard endpoint which includes popular_items from weekly_insights
        weeklyData = await fetchWithTimeout(
          apiClient.get('/analytics/dashboard', { params: baseParams }),
          15000
        );
        console.log('✅ 7-day dashboard data fetched with popular items');
        
        // AI insights are optional
        try {
          aiInsights = await fetchWithTimeout(
            apiClient.get('/analytics/top-performers-insights', { params: { ...baseParams, days: 7 } }),
            10000
          );
        } catch (e) {
          console.log('⚠️ AI insights not available');
        }
      } catch (err) {
        console.error('❌ Weekly fetch failed:', err);
      }
      
      try {
        console.log('📊 Fetching 30-day data...');
        monthlyResult = await fetchWithTimeout(
          apiClient.get('/analytics/dine-ai-impact', { params: { ...baseParams, days: 30 } }),
          15000
        );
        console.log('✅ 30-day data fetched');
      } catch (err) {
        console.error('❌ Monthly fetch failed:', err);
      }
      
      try {
        console.log('📊 Fetching 90-day data...');
        quarterlyResult = await fetchWithTimeout(
          apiClient.get('/analytics/dine-ai-impact', { params: { ...baseParams, days: 90 } }),
          15000
        );
        console.log('✅ 90-day data fetched');
      } catch (err) {
        console.error('❌ Quarterly fetch failed:', err);
      }
      
      const weeklyRes = { status: weeklyData ? 'fulfilled' : 'rejected', value: [weeklyData, aiInsights] };
      const monthlyRes = { status: monthlyResult ? 'fulfilled' : 'rejected', value: monthlyResult };
      const quarterlyRes = { status: quarterlyResult ? 'fulfilled' : 'rejected', value: quarterlyResult };
      
      console.log('📊 Fetch results:', {
        weekly: weeklyRes.status,
        monthly: monthlyRes.status,
        quarterly: quarterlyRes.status
      });

      // Results are already extracted above, no need to redeclare
      
      // Check if we have at least some data
      if (!weeklyData && !monthlyResult && !quarterlyResult) {
        throw new Error('All data fetches failed. Please check your connection.');
      }
      
      // Combine all data with fallbacks
      // The dine-ai-impact endpoint returns summary directly at top level
      const combinedData: any = {
        plan: weeklyData?.data?.plan || restaurantData?.subscription_plan || 'yummy',
        weekly: weeklyData ? {
          // Use dine_ai_impact from dashboard endpoint for summary metrics
          summary: weeklyData.data?.dine_ai_impact?.summary || {},
          date_range: weeklyData.data?.dine_ai_impact?.date_range || {},
          is_historical: weeklyData.data?.is_historical || false,
          data_notice: weeklyData.data?.data_notice || null,
          // Keep order_analytics for popular_items (This Week's Champions)
          order_analytics: {
            ...(weeklyData.data?.order_analytics || {}),
            ai_insights: aiInsights?.data?.ai_insights || null
          }
        } : null,
        monthly: monthlyResult?.data || null,
        quarterly: quarterlyResult?.data || null
      };

      setData(combinedData);
      console.log('✅ All periods data loaded successfully');
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCombinedData = async () => {
    try {
      console.log('📊 Fetching combined data for annual projection...', {
        hasSeasonalMenu: hasActiveSeasonalMenu,
        seasonalId: activeSeasonalMenuId
      });
      
      // Always fetch regular data
      const regularPromises = [
        apiClient.get('/analytics/dine-ai-impact', { 
          params: { menu_type: 'regular', days: 30 } 
        }).catch(() => null),
        apiClient.get('/analytics/dine-ai-impact', { 
          params: { menu_type: 'regular', days: 90 } 
        }).catch(() => null)
      ];
      
      // Only fetch seasonal if we have an active seasonal menu
      const seasonalPromises = hasActiveSeasonalMenu && activeSeasonalMenuId ? [
        apiClient.get('/analytics/dine-ai-impact', { 
          params: { menu_type: 'seasonal', days: 30, seasonal_menu_id: activeSeasonalMenuId } 
        }).catch(() => null),
        apiClient.get('/analytics/dine-ai-impact', { 
          params: { menu_type: 'seasonal', days: 90, seasonal_menu_id: activeSeasonalMenuId } 
        }).catch(() => null)
      ] : [null, null];
      
      const [regular30, regular90, seasonal30, seasonal90] = await Promise.all([
        ...regularPromises,
        ...seasonalPromises
      ]);
      
      // Combine 30-day data
      const combined30 = {
        summary: {
          revenue_generated: 
            (regular30?.data?.dine_ai_impact?.summary?.revenue_generated || 0) +
            (seasonal30?.data?.dine_ai_impact?.summary?.revenue_generated || 0),
          total_orders: 
            (regular30?.data?.dine_ai_impact?.summary?.total_orders || 0) +
            (seasonal30?.data?.dine_ai_impact?.summary?.total_orders || 0),
          staff_hours_saved: 
            (regular30?.data?.dine_ai_impact?.summary?.staff_hours_saved || 0) +
            (seasonal30?.data?.dine_ai_impact?.summary?.staff_hours_saved || 0),
          avg_order_value: 0 // Will be calculated
        },
        date_range: regular30?.data?.dine_ai_impact?.date_range
      };
      
      // Calculate combined AOV
      if (combined30.summary.total_orders > 0) {
        combined30.summary.avg_order_value = 
          combined30.summary.revenue_generated / combined30.summary.total_orders;
      }
      
      // Combine 90-day data
      const combined90 = {
        summary: {
          revenue_generated: 
            (regular90?.data?.dine_ai_impact?.summary?.revenue_generated || 0) +
            (seasonal90?.data?.dine_ai_impact?.summary?.revenue_generated || 0),
          total_orders: 
            (regular90?.data?.dine_ai_impact?.summary?.total_orders || 0) +
            (seasonal90?.data?.dine_ai_impact?.summary?.total_orders || 0),
          staff_hours_saved: 
            (regular90?.data?.dine_ai_impact?.summary?.staff_hours_saved || 0) +
            (seasonal90?.data?.dine_ai_impact?.summary?.staff_hours_saved || 0),
          avg_order_value: 0 // Will be calculated
        },
        date_range: regular90?.data?.dine_ai_impact?.date_range
      };
      
      // Calculate combined AOV
      if (combined90.summary.total_orders > 0) {
        combined90.summary.avg_order_value = 
          combined90.summary.revenue_generated / combined90.summary.total_orders;
      }
      
      setCombinedMonthlyData(combined30);
      setCombinedQuarterlyData(combined90);
      console.log('✅ Combined data calculated for annual projection:', {
        regular90Hours: regular90?.data?.dine_ai_impact?.summary?.staff_hours_saved || 0,
        seasonal90Hours: seasonal90?.data?.dine_ai_impact?.summary?.staff_hours_saved || 0,
        combined90Hours: combined90.summary.staff_hours_saved,
        annualProjection: combined90.summary.staff_hours_saved * 4 * 20
      });
    } catch (err) {
      console.error('Failed to fetch combined data:', err);
      // Fall back to current menu data if combined fetch fails
      setCombinedMonthlyData(null);
      setCombinedQuarterlyData(null);
    }
  };


  const handleMenuTypeChange = (type: 'regular' | 'seasonal') => {
    if (type === menuType) return;
    
    // Check if seasonal menu is available
    if (type === 'seasonal') {
      if (!hasActiveSeasonalMenu || !activeSeasonalMenuId) {
        console.log('No active seasonal menu available or ID not loaded');
        setError('Seasonal menu is still loading. Please try again.');
        return;
      }
    }
    
    setMenuType(type);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Fixed Header - Light Mode */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/restaurant-dashboard")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">
                Analytics Dashboard
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto pt-20">
        <div className="container mx-auto px-4 py-8">
        {/* Menu Type Tabs - Light Mode */}
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1 mb-8 inline-flex">
          <button
            onClick={() => handleMenuTypeChange('regular')}
            className={`px-4 py-1.5 rounded text-sm font-medium transition-all ${
              menuType === 'regular'
                ? "bg-[#FDB913] text-black font-semibold"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            Regular Menu
          </button>
          {hasActiveSeasonalMenu && (
            <button
              onClick={() => handleMenuTypeChange('seasonal')}
              className={`px-4 py-1.5 rounded text-sm font-medium transition-all ${
                menuType === 'seasonal'
                  ? "bg-[#FDB913] text-black font-semibold"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              {activeSeasonalMenuName}
            </button>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FDB913] mx-auto mb-4"></div>
            <p className="text-gray-500">Loading analytics...</p>
          </div>
        )}

        {/* Analytics Components */}
        {!loading && data && (
          <>
            {/* Section 1: Last 7 Days Performance */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Last 7 Days</h2>
              
              {/* Performance Metrics Cards */}
              {data.weekly && (
                <DineAIImpactDashboard
                  summary={data.weekly.summary}
                  dateRange="7"
                  isHistorical={data.weekly.is_historical}
                  dataNotice={data.weekly.data_notice}
                  loading={loading}
                />
              )}

              {/* The Weekly Champions */}
              {data.weekly?.order_analytics?.popular_items && (
                <TopPerformersSection
                  items={data.weekly.order_analytics.popular_items}
                  weekStarting={data.weekly.order_analytics.week_starting}
                  weekEnding={data.weekly.order_analytics.week_ending}
                  isHistorical={data.weekly.order_analytics.is_historical}
                  dataNotice={data.weekly.order_analytics.data_notice}
                  loading={loading}
                />
              )}

              {/* AI Performance Analysis - Interactive */}
              {data.weekly?.order_analytics?.ai_insights && (
                <AIPerformanceAnalysisInteractive
                  insights={data.weekly.order_analytics.ai_insights}
                  loading={loading}
                />
              )}
            </div>

            {/* Section 2: Extended Performance Cards */}
            <ExtendedPerformanceCards 
              monthlyData={data.monthly}
              quarterlyData={data.quarterly}
              combinedMonthlyData={combinedMonthlyData}
              combinedQuarterlyData={combinedQuarterlyData}
              hourlyRate={20} // Can be made configurable later
            />
          </>
        )}
        </div>
      </div>
    </div>
  );
}